import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  People,
  BookOnline,
  AccountBalance,
  TrendingUp,
  PendingActions,
  VideoCall,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { apiService } from '../services/api.service';
import { DashboardStats } from '../types';
import Layout from '../components/Layout';
import StatCard from '../components/StatCard';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [globalPeriod, setGlobalPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [revenueTrend, setRevenueTrend] = useState<any[]>([]);
  const [userGrowthTrend, setUserGrowthTrend] = useState<any[]>([]);
  const [bookingStatusData, setBookingStatusData] = useState<any[]>([]);

  const loadData = useCallback(async () => {
    try {
      const [
        statsData,
        revenueTrendData,
        userGrowthTrendData,
        bookingStatus,
      ] = await Promise.all([
        apiService.getDashboardStats(globalPeriod),
        apiService.getRevenueTrend(globalPeriod, globalPeriod === 'daily' ? 30 : globalPeriod === 'weekly' ? 12 : 12),
        apiService.getUserGrowthTrend(globalPeriod, globalPeriod === 'daily' ? 30 : globalPeriod === 'weekly' ? 12 : 12),
        apiService.getBookingStatusDistribution(),
      ]);
      setStats(statsData);
      setRevenueTrend(revenueTrendData);
      setUserGrowthTrend(userGrowthTrendData);
      setBookingStatusData(bookingStatus);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [globalPeriod]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const statCards = [
    {
      title: 'Total Users',
      value: (stats?.totalUsers || 0).toLocaleString(),
      icon: <People />,
      color: '#6366f1',
    },
    {
      title: 'Students',
      value: (stats?.students || 0).toLocaleString(),
      icon: <People />,
      color: '#10b981',
    },
    {
      title: 'Tutors',
      value: (stats?.tutors || 0).toLocaleString(),
      icon: <People />,
      color: '#f59e0b',
    },
    {
      title: globalPeriod === 'daily' ? 'Active Bookings' : `Bookings (${globalPeriod})`,
      value: (stats?.activeBookingsToday || 0).toLocaleString(),
      icon: <BookOnline />,
      color: '#8b5cf6',
    },
    {
      title: globalPeriod === 'daily' ? 'Revenue Today' : `Revenue (${globalPeriod})`,
      value: `₹${(stats?.revenueToday || 0).toLocaleString()}`,
      icon: <TrendingUp />,
      color: '#10b981',
    },
    {
      title: globalPeriod === 'daily' ? 'Commission' : `Comm. (${globalPeriod})`,
      value: `₹${(stats?.platformCommissionToday || 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`,
      icon: <AccountBalance />,
      color: '#6366f1',
    },
    {
      title: 'Pending',
      value: (stats?.pendingWithdrawalRequests || 0).toLocaleString(),
      icon: <PendingActions />,
      color: '#ef4444',
    },
    {
      title: 'Live Sessions',
      value: (stats?.activeSessionsNow || 0).toLocaleString(),
      icon: <VideoCall />,
      color: '#0ea5e9',
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Layout>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{
          mb: 4,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              Insights Overview
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Real-time data and performance metrics for the last {globalPeriod}.
            </Typography>
          </Box>
          <FormControl size="small" variant="outlined" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Select
              value={globalPeriod}
              onChange={(e) => setGlobalPeriod(e.target.value as any)}
              sx={{ borderRadius: 2, minWidth: { xs: 'none', sm: 140 }, bgcolor: 'background.paper', boxShadow: 1 }}
            >
              <MenuItem value="daily">Daily View</MenuItem>
              <MenuItem value="weekly">Weekly View</MenuItem>
              <MenuItem value="monthly">Monthly View</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 3,
            mb: 4
          }}
        >
          {statCards.map((card, index) => (
            <StatCard
              key={index}
              title={card.title}
              value={card.value}
              icon={card.icon}
              color={card.color}
            />
          ))}
        </Box>

        <Box sx={{ mb: 4 }}>
          {/* Revenue Trend Chart */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Revenue Trend</Typography>
                <Typography variant="caption" color="text.secondary">Financial performance over time</Typography>
              </Box>
            </Box>
            {revenueTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={revenueTrend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    tickFormatter={(value) => `₹${value >= 1000 ? (value / 1000).toFixed(0) + 'k' : value}`}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                    }}
                    formatter={(value: any) => `₹${Number(value).toLocaleString()}`}
                  />
                  <Legend verticalAlign="top" height={36} wrapperStyle={{ paddingBottom: '20px', fontSize: '12px' }} />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#6366f1"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#6366f1', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6 }}
                    name="Total Revenue"
                  />
                  <Line
                    type="monotone"
                    dataKey="platformCommission"
                    stroke="#10b981"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6 }}
                    name="Commission"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                <Typography color="textSecondary">No revenue data available</Typography>
              </Box>
            )}
          </Paper>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr', lg: '2fr 1.5fr' }, gap: 3 }}>
          {/* User Growth Trend Chart */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>User Growth</Typography>
                <Typography variant="caption" color="text.secondary">Registration trends</Typography>
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={userGrowthTrend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#6b7280' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#6b7280' }} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="total" stroke="#6366f1" strokeWidth={3} dot={false} name="Total Users" />
                <Line type="monotone" dataKey="students" stroke="#10b981" strokeWidth={3} dot={false} name="Students" />
                <Line type="monotone" dataKey="tutors" stroke="#f59e0b" strokeWidth={3} dot={false} name="Tutors" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          {/* Booking Status Distribution */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Booking Distribution</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>Status breakdown</Typography>
            {bookingStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={bookingStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="status"
                  >
                    {bookingStatusData.map((entry, index) => {
                      const colors = ['#6366f1', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
                      return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                    })}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none' }} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <Typography color="textSecondary">No booking data available</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Layout>
  );
};

export default Dashboard;

